/**
 * Color Swiper Functionality
 * Handles SwiperJS initialization for color swatch sections
 */

/* global Swiper */

class SuniceColorSwiper {
  constructor() {
    this.swiperInstances = new Map();
  }

  /**
   * @param {HTMLElement} swiperContainer
   * @returns {void}
   */
  initializeColorSwiper(swiperContainer) {
    // Validate swiperContainer is a valid Element
    if (!swiperContainer || !(swiperContainer instanceof Element) || !swiperContainer.isConnected) {
      return;
    }
    
    // Prevent double-initialization across different modules/instances
    if (swiperContainer.dataset.suniceColorSwiperInitialized === 'true') {
      return;
    }

    const existingId = swiperContainer.id;
    const containerId = existingId && existingId.trim().length > 0
      ? existingId
      : `color-swiper-${Date.now()}-${Math.floor(Math.random() * 1000)}`;

    // Ensure the swiper container always has an ID so we can reliably target it later
    if (!existingId || existingId.trim().length === 0) {
      swiperContainer.id = containerId;
    }

    // Check if this swiper is already initialized
    if (this.swiperInstances.has(containerId)) {
      return;
    }

    const wrapper = swiperContainer.querySelector('.color-swiper-wrapper');
    if (!wrapper) {
      return;
    }

    // Count color swatches first
    const colorSlides = wrapper.querySelectorAll('.color-swiper-slide');
    const colorCount = colorSlides.length;
    
    // Determine if loop can work - need more slides than what's visible
    // For loop to work properly, we need at least slidesPerView + 2 slides
    // On mobile (3 slides visible), need at least 5-6 for smooth loop
    // On desktop (6 slides visible), need at least 8 for smooth loop
    const canLoop = colorCount >= 5; // Enable loop if we have at least 5 colors (works on mobile with 3 visible)

    // ALWAYS USE SWIPERJS - NO FLEXBOX

    const nextBtn = /** @type {HTMLElement | null} */ (swiperContainer.querySelector('.color-swiper-button-next'));
    const prevBtn = /** @type {HTMLElement | null} */ (swiperContainer.querySelector('.color-swiper-button-prev'));

    // Check viewport width - only apply no-swatch-slider on screens > 749px
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const isDesktop = viewportWidth > 749;
    
    // Only disable SwiperJS on desktop (> 749px) when we have 6 or fewer colors
    // On mobile (<= 749px), always use SwiperJS for better touch experience
    if (colorCount <= 6 && isDesktop) {

      swiperContainer.classList.add('no-swatch-slider');

      if (nextBtn) {
        nextBtn.style.display = 'none';
        nextBtn.style.visibility = 'hidden';
        nextBtn.style.opacity = '0';
        nextBtn.classList.remove('swiper-button-next');
      }

      if (prevBtn) {
        prevBtn.style.display = 'none';
        prevBtn.style.visibility = 'hidden';
        prevBtn.style.opacity = '0';
        prevBtn.classList.remove('swiper-button-prev');
      }

      swiperContainer.classList.remove('swiper', 'color-swiper');
      wrapper.classList.remove('swiper-wrapper');
      colorSlides.forEach((slide) => {
        const slideElement = /** @type {HTMLElement} */ (slide);
        slideElement.classList.remove('swiper-slide');
      });

      // Still set up variant title updates even though SwiperJS isn't initialized
      this.setupVariantTitleUpdates(swiperContainer);

      // Mark as initialized so other modules don't try to re-init
      swiperContainer.dataset.suniceColorSwiperInitialized = 'true';

      return;
    }

    // Add SwiperJS classes dynamically to avoid conflicts
    swiperContainer.classList.remove('no-swatch-slider');
    swiperContainer.classList.add('swiper', 'color-swiper');
    wrapper.classList.add('swiper-wrapper');
    colorSlides.forEach((slide) => {
      const slideElement = /** @type {HTMLElement} */ (slide);
      slideElement.classList.add('swiper-slide');
    });

    const nextBtnId = nextBtn ? (nextBtn.id && nextBtn.id.trim().length > 0 ? nextBtn.id : `${containerId}-next`) : '';
    const prevBtnId = prevBtn ? (prevBtn.id && prevBtn.id.trim().length > 0 ? prevBtn.id : `${containerId}-prev`) : '';

    if (nextBtn) {
      if (!nextBtn.id || nextBtn.id.trim().length === 0) {
        nextBtn.id = nextBtnId;
      }
      nextBtn.classList.add('swiper-button-next');
    }

    if (prevBtn) {
      if (!prevBtn.id || prevBtn.id.trim().length === 0) {
        prevBtn.id = prevBtnId;
      }
      prevBtn.classList.add('swiper-button-prev');
    }

    // Ensure the container has proper constraints
    swiperContainer.style.width = '100%';
    swiperContainer.style.maxWidth = '100%';
    swiperContainer.style.boxSizing = 'border-box';

    // Show/hide navigation buttons based on color count
    if (colorCount > 6) {
      // Show navigation buttons for more than 6 colors
      if (nextBtn) {
        nextBtn.style.display = 'flex';
        nextBtn.style.visibility = 'visible';
        nextBtn.style.opacity = '1';
      }
      if (prevBtn) {
        prevBtn.style.display = 'flex';
        prevBtn.style.visibility = 'visible';
        prevBtn.style.opacity = '1';
      }
    } else {
      // Hide navigation buttons for 6 or fewer colors
      if (nextBtn) {
        nextBtn.style.display = 'none';
      }
      if (prevBtn) {
        prevBtn.style.display = 'none';
      }
    }

    // Check if we're on a collection page
    const isCollectionPage = window.location.pathname.includes('/collections/') || 
                           document.querySelector('results-list') !== null ||
                           document.body.classList.contains('template-collection');
    
    // Store collection page status on container for persistence
    if (isCollectionPage) {
      swiperContainer.dataset.isCollectionPage = 'true';
    } else {
      swiperContainer.dataset.isCollectionPage = 'false';
    }
    
    // Determine max slides per view for mobile breakpoints
    // Use 3 for collection pages, 4 for other pages
    const mobileMaxSlides = isCollectionPage ? 5 : 6;
    const mobileMaxSlidesTablet = isCollectionPage ? 8 : 4;
    const mobileMaxSlidesDesktop = isCollectionPage ? 7 : 6;
    
    // Calculate slide width dynamically before initialization
    const calculateSlideWidth = () => {
      // Get the first slide to measure
      const firstSlide = colorSlides[0];
      if (!firstSlide || !(firstSlide instanceof HTMLElement)) return null;
      
      // Temporarily add swiper-slide class to measure
      const wasSwiperSlide = firstSlide.classList.contains('swiper-slide');
      if (!wasSwiperSlide) {
        firstSlide.classList.add('swiper-slide');
      }
      
      // Get computed width including padding/border
      const computedStyle = window.getComputedStyle(firstSlide);
      const width = firstSlide.offsetWidth;
      const marginRight = parseFloat(computedStyle.marginRight) || 0;
      const totalWidth = width + marginRight;
      
      // Remove class if we added it
      if (!wasSwiperSlide) {
        firstSlide.classList.remove('swiper-slide');
      }
      
      return totalWidth;
    };
    
    // Calculate how many slides can fit based on container width and slide width
    const calculateSlidesPerView = (slideWidth) => {
      if (!slideWidth || slideWidth <= 0) {
        // Fallback to default calculation
        const defaultSlidesPerView = Math.min(colorCount, 6);
        return defaultSlidesPerView;
      }
      
      const containerWidth = swiperContainer.offsetWidth;
      const spaceBetween = 12; // Match the spaceBetween in config
      
      // Calculate how many slides fit: (container width + spaceBetween) / (slide width + spaceBetween)
      const slidesThatFit = Math.floor((containerWidth + spaceBetween) / (slideWidth + spaceBetween));
      
      // Ensure we don't show more slides than we have
      const slidesPerView = Math.min(slidesThatFit, colorCount);
      
      // Ensure at least 1 slide is visible
      return Math.max(1, slidesPerView);
    };
    
    // Calculate slide width and slides per view before creating config
    const slideWidth = calculateSlideWidth();
    const calculatedSlidesPerView = calculateSlidesPerView(slideWidth);
    
    // Color swiper configuration - Use calculated slides per view based on CSS width
    const defaultSlidesPerView = Math.min(colorCount, 6);
    const loopedSlidesCount = colorCount >= 2 ? Math.max(2, Math.min(defaultSlidesPerView, colorCount - 1)) : 0;
    
    const swiperConfig = {
      loop: false, // Disabled - stop at last slide
      slidesPerView: calculatedSlidesPerView, // Use calculated slides per view based on CSS width
      spaceBetween: 12, // Match .color-swatcher__options gap: 12px
      slidesPerGroup: 1, // Always slide one at a time
      watchOverflow: true, // Prevent swiping when all slides are visible
      resistance: true, // Add resistance at edges
      resistanceRatio: 0.85, // How much resistance (0-1, lower = more resistance)
      navigation: colorCount > 6 && nextBtnId && prevBtnId ? {
        nextEl: `#${nextBtnId}`,
        prevEl: `#${prevBtnId}`,
      } : false,
      allowTouchMove: true, // Allow swiping
      grabCursor: true, // Show grab cursor
      centeredSlides: false,
      freeMode: false,
      nested: true,
      watchSlidesProgress: false,
      watchSlidesVisibility: false,
      preventInteractionOnTransition: true,
      breakpoints: {
        320: {
          slidesPerView: 'auto', // Use auto to calculate based on slide width
          spaceBetween: 12,
          loop: false,
          slidesPerGroup: 1,
        },
        568: {
          slidesPerView: 'auto', // Use auto to calculate based on slide width
          spaceBetween: 12,
          loop: false,
          slidesPerGroup: 1,
        },
        768: {
          slidesPerView: 'auto', // Use auto to calculate based on slide width
          spaceBetween: 12,
          loop: false,
          slidesPerGroup: 1,
        },
        1024: {
          slidesPerView: 'auto', // Use auto to calculate based on slide width
          spaceBetween: 12,
          loop: false,
          slidesPerGroup: 1,
        },
        1440: {
          slidesPerView: 'auto', // Use auto to calculate based on slide width
          spaceBetween: 12,
          loop: false,
          slidesPerGroup: 1,
        },
      },
    };

    // Initialize color swiper
    const globalWindow = /** @type {Record<string, any>} */ (window);
    const SwiperConstructor = globalWindow.Swiper;

    if (typeof SwiperConstructor !== 'function') {
      return;
    }

    // Verify the container still exists and is valid before initializing
    const containerElement = document.getElementById(containerId);
    if (!containerElement || !(containerElement instanceof Element) || !containerElement.isConnected) {
      return;
    }


    let swiper;
    try {
      swiper = new SwiperConstructor(`#${containerId}`, swiperConfig);
      this.swiperInstances.set(containerId, swiper);
    } catch (err) {
      // Silently fail if initialization fails
      return;
    }
    
    // If swiper wasn't created, return early
    if (!swiper) {
      return;
    }
    
    // Update swiper with calculated slide width if available
    if (slideWidth && slideWidth > 0) {
      // Store slide width for reference
      swiperContainer.dataset.slideWidth = slideWidth.toString();
      
      // Update slides to use calculated width if needed
      setTimeout(() => {
        if (swiper && swiper.slides) {
          swiper.slides.forEach((slide) => {
            if (slide instanceof HTMLElement) {
              // Ensure consistent width
              slide.style.width = `${slideWidth}px`;
              slide.style.minWidth = `${slideWidth}px`;
              slide.style.maxWidth = `${slideWidth}px`;
            }
          });
          
          // Update swiper to recalculate
          if (swiper.update) {
            swiper.update();
          }
        }
      }, 50);
    }

    // Check if last slide is visible in viewport
    const isLastSlideVisible = () => {
      if (!swiper || !swiper.slides || swiper.slides.length === 0) return false;
      
      const lastSlideIndex = swiper.slides.length - 1;
      const lastSlide = swiper.slides[lastSlideIndex];
      
      if (!lastSlide) return false;
      
      // Check if last slide is in the visible viewport
      const lastSlideRect = lastSlide.getBoundingClientRect();
      const containerRect = swiperContainer.getBoundingClientRect();
      
      // Last slide is visible if it's within the container bounds
      const isVisible = lastSlideRect.right <= containerRect.right + 10; // 10px tolerance
      
      return isVisible;
    };
    
    // Handle slide change to disable navigation buttons at edges (when loop is disabled)
    swiper.on('slideChange', () => {
      const lastVisible = isLastSlideVisible();
      
      // Disable next button when last slide enters the slider
      if (nextBtn) {
        if (swiper.isEnd || lastVisible) {
          nextBtn.classList.add('swiper-button-disabled');
          nextBtn.style.opacity = '0.35';
          nextBtn.style.cursor = 'not-allowed';
          nextBtn.style.pointerEvents = 'none';
        } else {
          nextBtn.classList.remove('swiper-button-disabled');
          nextBtn.style.opacity = '1';
          nextBtn.style.cursor = 'pointer';
          nextBtn.style.pointerEvents = 'auto';
        }
      }
      
      // Disable prev button when at the beginning
      if (prevBtn) {
        if (swiper.isBeginning) {
          prevBtn.classList.add('swiper-button-disabled');
          prevBtn.style.opacity = '0.35';
          prevBtn.style.cursor = 'not-allowed';
          prevBtn.style.pointerEvents = 'none';
        } else {
          prevBtn.classList.remove('swiper-button-disabled');
          prevBtn.style.opacity = '1';
          prevBtn.style.cursor = 'pointer';
          prevBtn.style.pointerEvents = 'auto';
        }
      }
    });
    
    // Also check on transition end to catch when slide fully enters
    swiper.on('transitionEnd', () => {
      const lastVisible = isLastSlideVisible();
      
      if (nextBtn && (swiper.isEnd || lastVisible)) {
        nextBtn.classList.add('swiper-button-disabled');
        nextBtn.style.opacity = '0.35';
        nextBtn.style.cursor = 'not-allowed';
        nextBtn.style.pointerEvents = 'none';
      }
    });
    
    // Set initial button states
    const initialLastVisible = isLastSlideVisible();
    if (nextBtn && (swiper.isEnd || initialLastVisible)) {
      nextBtn.classList.add('swiper-button-disabled');
      nextBtn.style.opacity = '0.35';
      nextBtn.style.cursor = 'not-allowed';
      nextBtn.style.pointerEvents = 'none';
    }
    if (prevBtn && swiper.isBeginning) {
      prevBtn.classList.add('swiper-button-disabled');
      prevBtn.style.opacity = '0.35';
      prevBtn.style.cursor = 'not-allowed';
      prevBtn.style.pointerEvents = 'none';
    }

    // Optimized resize handler with debouncing for mobile performance
    let resizeTimeout = null;
    let lastResizeWidth = window.innerWidth;
    const handleResize = () => {
      const currentWidth = window.innerWidth;
      // Skip if width hasn't changed significantly (saves CPU on mobile)
      if (Math.abs(currentWidth - lastResizeWidth) < 50) return;
      lastResizeWidth = currentWidth;
      
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        if (swiper) {
          try {
            // Recalculate slide width on resize
            const recalculatedSlideWidth = calculateSlideWidth();
            if (recalculatedSlideWidth && recalculatedSlideWidth > 0) {
              swiperContainer.dataset.slideWidth = recalculatedSlideWidth.toString();
              
              // Update all slides with new width
              if (swiper.slides) {
                swiper.slides.forEach((slide) => {
                  if (slide instanceof HTMLElement) {
                    slide.style.width = `${recalculatedSlideWidth}px`;
                    slide.style.minWidth = `${recalculatedSlideWidth}px`;
                    slide.style.maxWidth = `${recalculatedSlideWidth}px`;
                  }
                });
              }
            }
            
            // Re-check collection page status in case it changed
            // Use stored value first, then re-check
            const storedIsCollection = swiperContainer.dataset.isCollectionPage === 'true';
            const currentIsCollectionPage = storedIsCollection ||
                                          window.location.pathname.includes('/collections/') || 
                                          document.querySelector('results-list') !== null ||
                                          document.body.classList.contains('template-collection');
            const currentMobileMaxSlides = currentIsCollectionPage ? 3 : 4;
            
            // Update breakpoint values to ensure they persist through loop recreation
            if (swiper.params && swiper.params.breakpoints) {
              const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
              
              // Update 320px breakpoint
              if (swiper.params.breakpoints[320]) {
                swiper.params.breakpoints[320].slidesPerView = Math.min(colorCount, currentMobileMaxSlides);
              }
              
              // Update 568px breakpoint
              if (swiper.params.breakpoints[568]) {
                swiper.params.breakpoints[568].slidesPerView = Math.min(colorCount, currentMobileMaxSlides);
              }
            }
            
            // Force slidesPerGroup to 1 on all breakpoints
            swiper.params.slidesPerGroup = 1;
            if (swiper.params.breakpoints) {
              Object.keys(swiper.params.breakpoints).forEach(breakpoint => {
                if (swiper.params.breakpoints[breakpoint]) {
                  swiper.params.breakpoints[breakpoint].slidesPerGroup = 1;
                }
              });
            }
            
            // Force update to apply breakpoint changes
            if (swiper.update) {
              swiper.update();
            }
            if (swiper.updateSlides) {
              swiper.updateSlides();
            }
          } catch (e) {
            // Ignore resize errors
          }
        }
      }, 300);
    };
    window.addEventListener('resize', handleResize, { passive: true });

    // Mark as initialized so other modules don't try to re-init
    swiperContainer.dataset.suniceColorSwiperInitialized = 'true';

    // Add click handlers to update variant title when color swatch is clicked
    this.setupVariantTitleUpdates(swiperContainer);

    return swiper;
  }

  /**
   * Sets up variant title updates when color swatches are clicked
   * @param {HTMLElement} swiperContainer
   */
  setupVariantTitleUpdates(swiperContainer) {
    const colorSwatches = swiperContainer.querySelectorAll('.color-swatch');
    if (!colorSwatches.length) return;

    // Find the product card container - try multiple selectors
    const productCard = swiperContainer.closest('.sunice-product-card') ||
                       swiperContainer.closest('.product-card') ||
                       swiperContainer.closest('[data-product-id]');
    
    if (!productCard) {
      // Try finding via color-swatcher parent
      const colorSwatcher = swiperContainer.closest('.color-swatcher');
      if (colorSwatcher) {
        const productId = colorSwatcher.getAttribute('data-product-id');
        if (productId) {
          const foundCard = document.querySelector(`.sunice-product-card[data-product-id="${productId}"]`) ||
                           document.querySelector(`[data-product-id="${productId}"]`);
          if (foundCard) {
            return this.setupVariantTitleForCard(foundCard, colorSwatches);
          }
        }
      }
      return;
    }

    this.setupVariantTitleForCard(productCard, colorSwatches);
  }

  /**
   * Sets up variant title updates for a specific product card
   * @param {HTMLElement} productCard
   * @param {NodeListOf<Element>} colorSwatches
   */
  setupVariantTitleForCard(productCard, colorSwatches) {
    // Find the variant title element
    const variantTitle = productCard.querySelector('.variant-title');
    if (!variantTitle) {
      return;
    }

    // Helper function to update variant title from a swatch
    const updateVariantTitleFromSwatch = (swatch) => {
      // Re-query variant title in case DOM changed
      const currentVariantTitle = productCard.querySelector('.variant-title');
      if (!currentVariantTitle) {
        return false;
      }
      
      const colorName = swatch.getAttribute('data-color') || 
                       swatch.getAttribute('title') || '';
      if (colorName) {
        currentVariantTitle.textContent = colorName;
        return true;
      } else {
        return false;
      }
    };

    // Set initial variant title to first available or active color
    const activeSwatch = Array.from(colorSwatches).find(s => 
      s.getAttribute('data-active') === 'true'
    ) || Array.from(colorSwatches).find(s => 
      s.getAttribute('data-option-available') === 'true'
    );
    
    if (activeSwatch) {
      updateVariantTitleFromSwatch(activeSwatch);
    } else if (colorSwatches.length > 0) {
      // Fallback to first swatch if no active/available found
      const firstSwatch = colorSwatches[0];
      if (firstSwatch) {
        updateVariantTitleFromSwatch(firstSwatch);
      }
    }

    // Add click handlers to all color swatches
    // Use a flag to prevent duplicate listeners
    const swiperContainer = colorSwatches[0]?.closest('.color-swiper-container');
    if (swiperContainer && swiperContainer.dataset.variantTitleSetup === 'true') {
      return; // Already set up
    }

    // Add click handlers
    colorSwatches.forEach((swatch) => {
      // Handle both click and touch events for mobile compatibility
      const handleColorSwatchClick = function(event) {
        // Update variant title immediately
        updateVariantTitleFromSwatch(this);
        
        // Update active state on all swatches in the same product card (not just same container)
        const allSwatches = productCard.querySelectorAll('.color-swatch');
        allSwatches.forEach((s) => {
          s.removeAttribute('data-active');
        });
        // Set data-active on the clicked swatch
        this.setAttribute('data-active', 'true');
      };
      
      // Use capture phase to ensure our handler runs early
      // Add both click and touchstart listeners for mobile support
      swatch.addEventListener('click', handleColorSwatchClick, { capture: true, passive: true });
      swatch.addEventListener('touchend', function(e) {
        handleColorSwatchClick.call(this, e);
      }, { capture: true, passive: true });
    });

    // Also use MutationObserver to watch for data-active changes as a fallback
    // This ensures the variant title updates even if click handlers don't fire
    // Watch the entire product card for any data-active changes
    if (!productCard.dataset.variantTitleObserver) {
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'data-active') {
            const target = mutation.target;
            if (target instanceof HTMLElement && 
                target.classList.contains('color-swatch') &&
                target.getAttribute('data-active') === 'true') {
              updateVariantTitleFromSwatch(target);
            }
          }
        });
      });

      // Observe all color swatches in this product card
      const allSwatches = productCard.querySelectorAll('.color-swatch');
      allSwatches.forEach((swatch) => {
        observer.observe(swatch, { attributes: true, attributeFilter: ['data-active'] });
      });

      // Store observer reference on product card to prevent duplicates
      productCard.dataset.variantTitleObserver = 'true';
    }

    // Mark as set up to prevent duplicate listeners
    if (swiperContainer) {
      swiperContainer.dataset.variantTitleSetup = 'true';
    }
  }

  destroyColorSwipers() {
    this.swiperInstances.forEach((swiper) => {
      if (swiper && swiper.destroy) {
        swiper.destroy(true, true);
      }
    });
    this.swiperInstances.clear();
  }
}

// Export for use in main swiper file
/** @type {any} */ (window).SuniceColorSwiper = SuniceColorSwiper;

// Global helper: automatically initialize color swipers, including when content is lazy-loaded
(() => {
  const globalWindow = /** @type {any} */ (window);

  // Create or reuse a shared singleton instance
  if (!globalWindow.SuniceColorSwiperInstance) {
    globalWindow.SuniceColorSwiperInstance = new SuniceColorSwiper();
  }
  const colorSwiperInstance = globalWindow.SuniceColorSwiperInstance;

  /**
   * Initialize any uninitialized color swipers within a root element.
   * @param {ParentNode} root
   */
  const initColorSwipersInRoot = (root) => {
    const containers = root.querySelectorAll
      ? root.querySelectorAll('.color-swiper-container')
      : [];

    containers.forEach((container) => {
      if (!(container instanceof HTMLElement)) return;

      // Skip if already initialized (by any instance/module)
      if (
        container.dataset.suniceColorSwiperInitialized === 'true' ||
        container.classList.contains('swiper') ||
        container.classList.contains('no-swatch-slider')
      ) {
        return;
      }

      colorSwiperInstance.initializeColorSwiper(container);
    });
  };

  // Initial run - initialize all swipers, defer non-critical work
  const runInitial = () => {
    try {
      // Initialize all swipers immediately (reliability over lazy loading)
      initColorSwipersInRoot(document);
      
      // Defer variant title setup (non-critical for initial render)
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          setupVariantTitleForNonSwiperSwatches(document);
        }, { timeout: 2000 });
      } else {
        setTimeout(() => {
          setupVariantTitleForNonSwiperSwatches(document);
        }, 100);
      }
    } catch (e) {
      // Error during initial Sunice color swiper initialization
    }
  };

  /**
   * Sets up variant title updates for color swatches that are NOT in a swiper container
   * (e.g., in variant picker forms)
   * @param {ParentNode} root
   */
  const setupVariantTitleForNonSwiperSwatches = (root) => {
    // Find all color swatches that are NOT inside a color-swiper-container
    // OR are in a no-swatch-slider container (which might not have been set up)
    const allColorSwatches = root.querySelectorAll('.color-swatch');
    
    allColorSwatches.forEach((swatch) => {
      const swiperContainer = swatch.closest('.color-swiper-container');
      
      // Skip if it's in a swiper container that has been set up (handled by setupVariantTitleUpdates)
      // But include no-swatch-slider containers that might not have been set up yet
      if (swiperContainer && swiperContainer.dataset.variantTitleSetup === 'true') {
        return;
      }
      
      // Also skip if it's in a swiper container that's been initialized (unless it's no-swatch-slider)
      if (swiperContainer && 
          swiperContainer.dataset.suniceColorSwiperInitialized === 'true' &&
          !swiperContainer.classList.contains('no-swatch-slider')) {
        return;
      }

      // Skip if already set up
      if (swatch.dataset.variantTitleSetup === 'true') {
        return;
      }

      // Find the product card
      const productCard = swatch.closest('.sunice-product-card') ||
                         swatch.closest('.product-card') ||
                         swatch.closest('[data-product-id]');
      
      if (!productCard) return;

      // Find the variant title element
      const variantTitle = productCard.querySelector('.variant-title');
      if (!variantTitle) {
        return;
      }

      // Set initial variant title if not already set
      if (!variantTitle.dataset.initialized) {
        const activeSwatch = productCard.querySelector('.color-swatch[data-active="true"]') ||
                            productCard.querySelector('.color-swatch[data-option-available="true"]');
        
        if (activeSwatch) {
          const colorName = activeSwatch.getAttribute('data-color') || 
                           activeSwatch.getAttribute('title') || '';
          if (colorName) {
            variantTitle.textContent = colorName;
            variantTitle.dataset.initialized = 'true';
          }
        } else {
          // Find first available color swatch in the product card
          const firstAvailable = productCard.querySelector('.color-swatch[data-option-available="true"]');
          if (firstAvailable) {
            const colorName = firstAvailable.getAttribute('data-color') || 
                             firstAvailable.getAttribute('title') || '';
            if (colorName) {
              variantTitle.textContent = colorName;
              variantTitle.dataset.initialized = 'true';
            }
          }
        }
      }

      // Add click handler with mobile touch support
      const handleColorSwatchClick = function(event) {
        // Don't stop propagation - just update the variant title
        const colorName = this.getAttribute('data-color') || 
                         this.getAttribute('title') || '';
        if (colorName && variantTitle) {
          variantTitle.textContent = colorName;
          
          // Update active state on all color swatches in the same product card
          const allSwatches = productCard.querySelectorAll('.color-swatch');
          allSwatches.forEach((s) => {
            s.removeAttribute('data-active');
          });
          this.setAttribute('data-active', 'true');
        }
      };
      
      // Use capture phase to ensure our handler runs early
      // Add both click and touchstart listeners for mobile support
      swatch.addEventListener('click', handleColorSwatchClick, { capture: true, passive: true });
      swatch.addEventListener('touchend', function(e) {
        handleColorSwatchClick.call(this, e);
      }, { capture: true, passive: true });
      
      // Also set up MutationObserver for this swatch if not already done
      if (!productCard.dataset.variantTitleObserver) {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (mutation.type === 'attributes' && mutation.attributeName === 'data-active') {
              const target = mutation.target;
              if (target instanceof HTMLElement && 
                  target.classList.contains('color-swatch') &&
                  target.getAttribute('data-active') === 'true') {
                const colorName = target.getAttribute('data-color') || 
                                 target.getAttribute('title') || '';
                if (colorName && variantTitle) {
                  variantTitle.textContent = colorName;
                }
              }
            }
          });
        });
        
        const allSwatches = productCard.querySelectorAll('.color-swatch');
        allSwatches.forEach((s) => {
          observer.observe(s, { attributes: true, attributeFilter: ['data-active'] });
        });
        
        productCard.dataset.variantTitleObserver = 'true';
      }

      // Mark as set up
      swatch.dataset.variantTitleSetup = 'true';
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', runInitial, { once: true });
  } else {
    runInitial();
  }

  // Optimized MutationObserver with debouncing for mobile performance
  let mutationTimeout = null;
  
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (!(node instanceof HTMLElement)) return;

        // If the node itself is a color-swiper container
        if (node.classList.contains('color-swiper-container')) {
          initColorSwipersInRoot(node.parentNode || node);
        } else {
          // Or if it contains any
          initColorSwipersInRoot(node);
        }

        // Defer variant title setup (non-critical)
        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => {
            setupVariantTitleForNonSwiperSwatches(node);
          }, { timeout: 1000 });
        } else {
          // Debounce to batch updates
          if (mutationTimeout) clearTimeout(mutationTimeout);
          mutationTimeout = setTimeout(() => {
            setupVariantTitleForNonSwiperSwatches(node);
          }, 50);
        }
      });
    }
  });

  if (document.body) {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  } else {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        if (document.body) {
          observer.observe(document.body, {
            childList: true,
            subtree: true,
          });
        }
      },
      { once: true }
    );
  }
})();
